import type { SharedFolderDeleteMessageDatagramBuf } from "~/classes/generated/message"
import type { SharedFolderDeleteResponseDatagramBuf } from "~/classes/generated/response"

import { StaticFile } from "~/classes/file/StaticFile"
import { DatagramBuf } from "~/classes/generated/datagram"
import { RequestDatagramBuf, SharedFolderDeleteRequestDatagramBuf } from "~/classes/generated/request"
import { DatagramBufferBuilder } from "~/utils/flatbuffers/builder"
import { addRequestIdToBuf, parseDatagramError, parseMessageId, parseRequestId, parseSenderId } from "~/utils/flatbuffers/datagram"
import { parseFile } from "~/utils/flatbuffers/file"
import { addSharedFolderIdToBuf, parseSharedFolderId } from "~/utils/flatbuffers/id"
import { maybe } from "~/utils/helpers"

const console = useLogger("sharedFolder/delete")

export function sendSharedFolderDeleteRequests(requests: SharedFolderDeleteRequestDatagram[]) {
  const { sendWorkerDatagramBuf } = useCache()

  const builder = new DatagramBufferBuilder(64)
  const deleteRequests = []
  for (const request of requests) {
    const requestId = addRequestIdToBuf(builder, request.requestId)
    const sharedFolderId = addSharedFolderIdToBuf(builder, request.sharedFolderId)

    SharedFolderDeleteRequestDatagramBuf.startSharedFolderDeleteRequestDatagramBuf(builder)
    SharedFolderDeleteRequestDatagramBuf.addRequestId(builder, requestId)
    SharedFolderDeleteRequestDatagramBuf.addOffline(builder, request.offline)
    SharedFolderDeleteRequestDatagramBuf.addSharedFolderId(builder, sharedFolderId)

    const deleteRequest = SharedFolderDeleteRequestDatagramBuf.endSharedFolderDeleteRequestDatagramBuf(builder)

    RequestDatagramBuf.startRequestDatagramBuf(builder)
    RequestDatagramBuf.addSharedFolderDeleteRequest(builder, deleteRequest)
    deleteRequests.push(RequestDatagramBuf.endRequestDatagramBuf(builder))
  }

  const del = DatagramBuf.createRequestVector(builder, deleteRequests)
  DatagramBuf.startDatagramBuf(builder)
  DatagramBuf.addRequest(builder, del)
  builder.finish(DatagramBuf.endDatagramBuf(builder))
  sendWorkerDatagramBuf(builder.asUint8Array())
}

/** Parse a Flatbuffers representation of a response */
export function handleSharedFolderDeleteResponseDatagramBuf(datagram: SharedFolderDeleteResponseDatagramBuf) {
  onSharedFolderDeleteResponse({
    requestId: parseRequestId(datagram.requestId()!),
    error: maybe(parseDatagramError)(datagram.error()),
    offline: datagram.offline(),
    home: maybe(parseFile)(datagram.home()),
    archive: maybe(parseFile)(datagram.archive()),
    sharedFolderId: parseSharedFolderId(datagram.sharedFolderId()!),
  })
}

export function onSharedFolderDeleteResponse(response: SharedFolderDeleteResponseDatagram) {
  const { emitEntityEvent, emitInterfaceEvent } = useEvents()

  console.debug("Received sharedFolder delete", clone(response))

  emitInterfaceEvent("sharedFolderDeleteResponse", response, response.requestId)

  if (response.error)
    return onSharedFolderDeleteErrored(response.error)
  if (!response.home || !response.archive)
    throw new Error("No home / archive in delete response")

  const event: SharedFolderEvent = {
    sharedFolderId: response.sharedFolderId,
    home: response.home,
    archive: response.archive,
  }

  emitEntityEvent("sharedFolderDeleted", response.sharedFolderId, event, response.offline)
}

function onSharedFolderDeleteErrored(error: DatagramError) {
  const { notifyDatagramError } = useAlerts()

  return notifyDatagramError(error, "SharedFolder delete request was unsuccessful")
}

export function handleSharedFolderDeleteMessageDatagramBuf(datagram: SharedFolderDeleteMessageDatagramBuf) {
  onSharedFolderDeleteMessage({
    messageId: parseMessageId(datagram.messageId()!),
    senderId: parseSenderId(datagram.senderId()!),
    home: parseFile(datagram.home()!),
    archive: parseFile(datagram.archive()!),
  })
}

export function onSharedFolderDeleteMessage(message: SharedFolderDeleteMessageDatagram) {
  const { emitEntityEvent } = useEvents()

  console.debug("Received sharedFolder delete message", clone(message))

  const home = new StaticFile(message.home)
  const sharedFolderId = home.rootSharedFolderId()
  if (!sharedFolderId)
    throw new Error("Shared folder delete message did not contain a shared folder ID")
  const event: SharedFolderEvent = { sharedFolderId, home: message.home, archive: message.archive }

  emitEntityEvent("sharedFolderDeleted", sharedFolderId, event, false)
}
